<!-- ProgressBar.vue -->
<!-- Forensic progress bar with activity density heatmap and event markers. -->
<!-- Heatmap buckets come from GET /sessions/{id}/timeline via playerStore. -->
<template>
  <div
    ref="container"
    class="w-full h-[36px] relative flex items-center cursor-pointer select-none px-4 progress-container"
    @mousemove="onHover"
    @mouseleave="hideTooltip"
    @mousedown="startDrag"
  >

    <!-- Timestamp tooltip -->
    <div
      v-show="tooltip.visible"
      class="absolute bottom-full mb-2 px-3 py-1.5 bg-black/90 text-white text-xs rounded whitespace-nowrap pointer-events-none z-50"
      :style="{ left: tooltip.x + 'px', transform: 'translateX(-50%)' }"
    >
      {{ tooltip.label }}
    </div>

    <!-- Bar background -->
    <div
      ref="bar"
      class="w-full h-[12px] bg-white/15 relative overflow-visible progress-background"
    >

      <!-- Density heatmap -->
      <div v-if="hasHeatmap" class="absolute inset-0 flex">
        <div
          v-for="(bucket, i) in player.activityHeatmap"
          :key="i"
          class="heatmap-segment"
          :style="{
            width: bucketWidth + '%',
            background: densityColor(bucket.intensity),
            opacity: bucket.timestamp < smoothTime ? 0.35 : 1,
          }"
        ></div>
      </div>

      <!-- Buffered bar (HLS only, shown when no heatmap) -->
      <div
        v-if="!player.isGuacMode && player.bufferedPct > 0 && !hasHeatmap"
        class="absolute left-0 top-0 bottom-0 bg-white/40"
        :style="{ width: player.bufferedPct + '%' }"
      ></div>

      <!-- Played progress — manipulated directly via DOM ref, bypassing Vue reactivity -->
      <div
        ref="progressEl"
        class="absolute left-0 top-0 bottom-0 bg-accent pointer-events-none z-10"
        style="width: 0%"
      >
        <!-- Scrubber handle -->
        <div
          class="absolute right-[-14px] top-1/2 -translate-y-1/2
                 w-[28px] h-[28px] rounded-full bg-accent
                 border-[3px] border-white
                 shadow-[0_4px_12px_rgba(0,0,0,0.5)]
                 pointer-events-none z-20 progress-handle"
        ></div>
      </div>

      <!-- TTP detection pins — only show detections, not commands/keystrokes -->
      <template v-for="(marker, i) in visibleMarkers" :key="i">
        <div
          v-if="marker.type === 'detection'"
          class="ttp-pin"
          :style="{ left: markerLeft(marker.t) + '%' }"
          :title="marker.label"
          @click.stop="seekToMarker(marker)"
        >
          <div class="ttp-pin-line"></div>
          <div class="ttp-pin-diamond"></div>
        </div>
      </template>

    </div>

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { usePlayerStore }  from "../stores/playerStore";
import { useSessionStore } from "../stores/sessionStore";

// ---------------------------------------------------------------------------
// Stores
// ---------------------------------------------------------------------------

const player   = usePlayerStore();
const sessions = useSessionStore();

// ---------------------------------------------------------------------------
// Template refs
// ---------------------------------------------------------------------------

const container  = ref(null);
const bar        = ref(null);
const progressEl = ref(null);   // direct DOM ref — bypasses Vue reactivity for 60fps animation

// ---------------------------------------------------------------------------
// Tooltip
// ---------------------------------------------------------------------------

const tooltip = ref({ visible: false, x: 0, label: "0:00" });

// ---------------------------------------------------------------------------
// Drag state
// ---------------------------------------------------------------------------

const dragging = ref(false);

// ---------------------------------------------------------------------------
// Smooth position interpolation via requestAnimationFrame
//
// Instead of jumping to player.currentTime on each player event,
// we interpolate smoothly using real elapsed wall-clock time.
// This makes the scrubber fluid at 60fps regardless of how often
// the player fires timeupdate / onprogress events.
// ---------------------------------------------------------------------------

const smoothTime = ref(0);
let   _rafId     = null;
let   _lastRaf   = null;

function _startRaf() {
  if (_rafId) return;

  function tick(now) {
    _rafId = requestAnimationFrame(tick);

    if (!player.isPlaying || !player.duration) {
      _lastRaf = null;
      return;
    }

    if (_lastRaf === null) {
      _lastRaf = now;
      return;
    }

    const elapsed = (now - _lastRaf) / 1000;
    _lastRaf = now;

    let next = smoothTime.value + elapsed;

    // Drift correction — only sync forward, never backward.
    // Backward corrections are what cause the visible "jitter" in Guac
    // because the Guacamole player occasionally reports slightly stale
    // positions. We clamp: if we're ahead of the player by more than 1s,
    // gently ease back; if we're behind, snap forward.
    const playerT = player.currentTime;
    const drift   = next - playerT;

    if (drift > 1.0) {
      // Too far ahead — ease back gently (never jump)
      next = next - drift * 0.05;
    } else if (drift < -0.5) {
      // Too far behind — snap forward to catch up
      next = playerT;
    }
    // Within -0.5s..+1.0s: trust the rAF clock, ignore player events

    const clamped = Math.min(Math.max(next, 0), player.duration);
    smoothTime.value = clamped;

    // Write directly to DOM — no Vue reactive overhead at 60fps
    if (progressEl.value && player.duration > 0) {
      progressEl.value.style.width = ((clamped / player.duration) * 100) + "%";
    }
  }

  _rafId = requestAnimationFrame(tick);
}

function _stopRaf() {
  if (_rafId) { cancelAnimationFrame(_rafId); _rafId = null; }
  _lastRaf = null;
}

function _syncDom(t) {
  smoothTime.value = t;
  if (progressEl.value && player.duration > 0) {
    progressEl.value.style.width = ((t / player.duration) * 100) + "%";
  }
}

// Sync smoothTime only when NOT playing (seek, load, pause).
// While the rAF loop is running we never let player.currentTime
// override smoothTime directly — that is what causes the jitter.
watch(() => player.currentTime, (t) => {
  if (_rafId) return;   // rAF owns smoothTime while playing
  _syncDom(t);
  _lastRaf = null;
}, { immediate: true });

watch(() => player.isPlaying, (playing) => {
  if (playing) {
    _syncDom(player.currentTime);
    _lastRaf = null;
    _startRaf();
  } else {
    _stopRaf();
    _syncDom(player.currentTime);
  }
});

// ---------------------------------------------------------------------------
// Computed
// ---------------------------------------------------------------------------

const progressPct = computed(() => {
  if (!player.duration) return 0;
  return (smoothTime.value / player.duration) * 100;
});

const hasHeatmap = computed(
  () => player.activityHeatmap?.length > 0
);

const bucketWidth = computed(() => {
  if (!player.activityHeatmap?.length) return 0;
  return 100 / player.activityHeatmap.length;
});

const visibleMarkers = computed(() => {
  const markers = player.activityMarkers ?? [];
  if (!player.duration || !markers.length) return [];
  return markers
    .filter((m) => m.t <= player.duration * 1000)
    .slice(0, 200);
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmt(seconds) {
  if (!seconds || isNaN(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

function calcTime(clientX) {
  const rect = bar.value.getBoundingClientRect();
  const pos  = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  return pos * player.duration;
}

function densityColor(intensity) {
  const i = Math.max(0, Math.min(1, intensity));
  if (i < 0.5) {
    const r = Math.round(100 + 155 * (i * 2));
    return `rgb(${r},200,50)`;
  }
  const ratio = (i - 0.5) * 2;
  return `rgb(255,${Math.round(200 - 150 * ratio)},50)`;
}

function markerLeft(tMs) {
  if (!player.duration) return 0;
  return Math.min(100, (tMs / 1000 / player.duration) * 100);
}



// ---------------------------------------------------------------------------
// Interactions
// ---------------------------------------------------------------------------

function onHover(e) {
  if (!bar.value) return;

  const rect = bar.value.getBoundingClientRect();
  const time = calcTime(e.clientX);

  tooltip.value = {
    visible: true,
    x:       e.clientX - rect.left,
    label:   fmt(time),
  };

  if (dragging.value) {
    _syncDom(time);
    emitSeek(time);
  }
}

function hideTooltip() {
  if (!dragging.value) tooltip.value.visible = false;
}

function startDrag(e) {
  dragging.value = true;

  const onMove = (ev) => onHover(ev);
  const onUp   = () => {
    dragging.value        = false;
    tooltip.value.visible = false;
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup",   onUp);
  };

  window.addEventListener("mousemove", onMove);
  window.addEventListener("mouseup",   onUp);
  onHover(e);
}

function emitSeek(time) {
  window.dispatchEvent(
    new CustomEvent("player-seek", { detail: { time } })
  );
}

function seekToMarker(marker) {
  const t = marker.t / 1000;
  emitSeek(Math.max(0, t - 1));
}

// ---------------------------------------------------------------------------
// Load timeline when session / duration is ready
// ---------------------------------------------------------------------------

let debounce = null;

function tryLoad() {
  if (debounce) clearTimeout(debounce);
  debounce = setTimeout(async () => {
    const s = sessions.currentSession;
    if (!s || !player.duration || player.activityLoading || player.isInitializing) return;
    await player.loadActivityData(s.id, player.duration);
  }, 600);
}

// Snap smoothTime immediately when a seek is dispatched
// so the progress bar doesn't lag behind during the pause→seek sequence
function _onSeek(e) {
  const t = e.detail?.time;
  if (t != null) {
    _stopRaf();
    _syncDom(Math.max(0, Math.min(t, player.duration || t)));
  }
}

onMounted(() => {
  tryLoad();
  window.addEventListener("player-seek", _onSeek);
});

onUnmounted(() => {
  _stopRaf();
  window.removeEventListener("player-seek", _onSeek);
});

watch(() => sessions.currentSession, () => tryLoad());
watch(() => player.duration,         () => tryLoad());
</script>

<style scoped>
.progress-container {
  transition: height 0.2s ease;
}

.progress-container:hover .progress-background {
  height: 16px !important;
}

.heatmap-segment {
  height: 100%;
  transition: opacity 0.3s ease;
}

.progress-handle {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.progress-container:hover .progress-handle {
  transform: translateY(0%) scale(1.15) !important;
  box-shadow: 0 0 0 4px rgba(var(--accent-rgb), 0.3), 0 4px 16px rgba(0,0,0,0.6);
}

.ttp-pin {
  position: absolute;
  top: -6px;
  bottom: -6px;
  width: 20px;
  transform: translateX(-50%);
  z-index: 30;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

.ttp-pin-line {
  width: 2px;
  flex: 1;
  background: #ef4444;
  opacity: 1;
  box-shadow: 0 0 4px rgba(239, 68, 68, 0.8);
}

.ttp-pin-diamond {
  width: 12px;
  height: 12px;
  background: #ef4444;
  transform: rotate(45deg);
  flex-shrink: 0;
  margin-top: -6px;
  box-shadow: 0 0 8px rgba(239, 68, 68, 0.9), 0 0 16px rgba(239, 68, 68, 0.4);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  border: 1.5px solid rgba(255, 255, 255, 0.4);
}

.ttp-pin:hover .ttp-pin-diamond {
  transform: rotate(45deg) scale(1.4);
  box-shadow: 0 0 12px rgba(239, 68, 68, 1), 0 0 24px rgba(239, 68, 68, 0.6);
}

.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: var(--accent) transparent;
}
</style>